import { useEffect, useLayoutEffect, useRef } from 'react';
import { useAccount } from './AccountContext.jsx';

// Wires a game <iframe> into progress sync (progressSync.js).
//
// The prerendered HTML already carries the game's src, so the browser starts
// loading it before React runs. For a signed-in player whose first sync isn't
// done yet, the frame is parked on about:blank and pointed back at the game
// once it is, so the game reads the synced progress (and never saves stale
// values over it). Everyone else is untouched.
//
// Returns { ref, onLoad(cb) }: put `ref` on the iframe and use
// `onLoad(yourHandler)` as its onLoad; the handler only runs for the game
// itself, not for the about:blank hold.
export default function useGameFrame(src, gameId, gameName) {
  const ref = useRef(null);
  const held = useRef(false);
  const { progressReady, startGame, stopGame } = useAccount();

  // Before paint, so a parked frame never shows a half-booted game.
  useLayoutEffect(() => {
    if (!progressReady && ref.current) {
      held.current = true;
      ref.current.src = 'about:blank';
    }
    // Only on mount: later changes are handled below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (progressReady && held.current && ref.current) {
      held.current = false;
      ref.current.src = src;
    }
  }, [progressReady, src]);

  useEffect(() => () => stopGame(gameId), [gameId, stopGame]);

  const onLoad = (cb) => (event) => {
    const frame = ref.current;
    if (held.current || !frame || !frame.src || frame.src.startsWith('about:')) return;
    startGame(gameId, gameName);
    if (cb) cb(event);
  };

  return { ref, onLoad };
}
